import { useState, useEffect } from 'react'
import { cn } from '@/lib/utils'

// NavItem component used by both desktop and mobile menus
interface NavItemProps {
  href: string
  label: string
  hasChildren?: boolean
  onClick?: () => void
  isMobile?: boolean
  isTabletLandscape?: boolean
  dropdownItems?: {href: string; label: string}[]
}

function NavItem({ href, label, hasChildren = false, onClick, isMobile = false, isTabletLandscape = false, dropdownItems = [] }: NavItemProps) {
  const isActive = window.location.pathname === href
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)

  // Toggle dropdown for mobile
  const toggleDropdown = (e: React.MouseEvent) => {
    if (isMobile && hasChildren) {
      e.preventDefault()
      setIsDropdownOpen(!isDropdownOpen)
    }
  }

  return (
    <li className={cn("relative group", isMobile && "w-full")}>
      <a 
        href={href} 
        className={cn(
          "hover:text-gray-600 py-2 block uppercase relative font-secondary",
          isActive ? "text-[#4caf50] after:content-[''] after:absolute after:left-0 after:bottom-0 after:w-full after:h-[3px] after:bg-black" : 
            "after:content-[''] after:absolute after:left-0 after:bottom-0 after:w-0 after:h-[3px] after:bg-black after:transition-all after:duration-300 hover:after:w-full",
          hasChildren && "flex items-center",
          isMobile ? "text-black text-xl px-0" : 
            isTabletLandscape ? "text-black text-sm px-2" : 
            "text-black text-sm md:text-md lg:text-lg px-4"
        )}
        onClick={(e) => {
          if (isMobile && hasChildren) {
            toggleDropdown(e)
          } else {
            // Handle client-side navigation
            e.preventDefault()
            window.history.pushState({}, '', href)
            // Dispatch event to notify Main component about path change
            window.dispatchEvent(new Event('popstate'))
            // Scroll to top of page
            window.scrollTo(0, 0)
            if (onClick) onClick()
          }
        }}
      >
        {label}
        {hasChildren && (
          <svg className={cn("w-4 h-4 ml-1 mt-0.5 transition-transform duration-300", 
                            isMobile && isDropdownOpen && "transform rotate-180")} 
               fill="none" 
               stroke="currentColor" 
               viewBox="0 0 24 24" 
               xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        )}
      </a>

      {/* Desktop dropdown */}
      {!isMobile && hasChildren && dropdownItems.length > 0 && (
        <div className="absolute left-0 mt-0 w-56 origin-top-left bg-beaver-green rounded-b-md shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 z-50">
          <div className="py-1">
            {dropdownItems.map((item, index) => (
              <a
                key={index}
                href={item.href}
                className="block px-4 py-2 text-sm text-gray-800 hover:bg-beaver-green/10 hover:text-white"
                onClick={(e) => {
                  e.preventDefault()
                  window.history.pushState({}, '', item.href)
                  window.dispatchEvent(new Event('popstate'))
                  window.scrollTo(0, 0)
                  if (onClick) onClick()
                }}
              >
                {item.label}
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Mobile dropdown */}
      {isMobile && hasChildren && dropdownItems.length > 0 && (
        <div 
          className={cn(
            "bg-header-green/90 overflow-hidden transition-all duration-300 ease-in-out pl-4",
            isDropdownOpen ? "max-h-[500px] mt-2 mb-2" : "max-h-0"
          )}
        >
          {dropdownItems.map((item, index) => (
            <a
              key={index}
              href={item.href}
              className="block py-2 text-base text-gray-800 hover:text-beaver-green"
              onClick={(e) => {
                e.preventDefault()
                window.history.pushState({}, '', item.href)
                window.dispatchEvent(new Event('popstate'))
                window.scrollTo(0, 0)
                if (onClick) onClick()
              }}
            >
              {item.label}
            </a>
          ))}
        </div>
      )}
    </li>
  )
}

export function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 0
  )
  const [isPortrait, setIsPortrait] = useState(
    typeof window !== 'undefined' ? window.matchMedia('(orientation: portrait)').matches : false
  )
  
  // Service dropdown items
  const serviceGalleryItems = [
    { href: '/services/decks', label: 'Decks' },
    { href: '/services/pergolas-and-privacy-walls', label: 'Pergolas & Privacy Walls' },
    { href: '/services/living-spaces', label: 'Living Spaces' },
    { href: '/services/railings', label: 'Railings' },
    { href: '/services/fences', label: 'Fences' },
    { href: '/services/landscaping', label: 'Landscaping' }
  ]
  
  // // Gallery dropdown items
  // const galleryItems = [
  //   { href: '/gallery/decks', label: 'Decks' },
  //   { href: '/gallery/pergolas-and-privacy-walls', label: 'Pergolas & Privacy Walls' },
  //   { href: '/gallery/living-spaces', label: 'Living Spaces' },
  //   { href: '/gallery/railings', label: 'Railings' },
  //   { href: '/gallery/fences', label: 'Fences' },
  //   { href: '/gallery/landscaping', label: 'Landscaping' }
  // ]
  
  // Track window size and orientation for responsive adjustments
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth)
    }
    
    const handleOrientationChange = () => {
      setIsPortrait(window.matchMedia('(orientation: portrait)').matches)
    }
    
    window.addEventListener('resize', handleResize)
    window.addEventListener('resize', handleOrientationChange)
    window.addEventListener('orientationchange', handleOrientationChange)
    
    return () => {
      window.removeEventListener('resize', handleResize)
      window.removeEventListener('resize', handleOrientationChange)
      window.removeEventListener('orientationchange', handleOrientationChange)
    }
  }, [])
  
  // Setup navigation scroll behavior
  useEffect(() => {
    // Ensure scrolling to top on navigation
    const handlePopState = () => {
      window.scrollTo(0, 0)
    }
    
    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [])
  
  // Use a higher breakpoint (1024px) to capture tablets
  // Also force mobile menu in portrait orientation regardless of width
  const isMobile = windowWidth < 1024 || isPortrait 
  
  // Detect tablet in landscape mode
  const isTabletLandscape = !isPortrait && windowWidth >= 1024 && windowWidth <= 1800
  
  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen)
  }
  
  const closeMobileMenu = () => {
    setMobileMenuOpen(false)
  }
  
  // Logo with responsive sizing
  const Logo = () => {
    // Determine logo size based on device type and orientation
    let logoWidth = 475; // Default desktop size
    
    if (isMobile) {
      logoWidth = 250; // Mobile size
    } else if (isTabletLandscape) {
      logoWidth = 300; // Tablet in landscape mode
    }
    
    return (
      <div className="flex flex-col">
        <a 
          href="/" 
          className="flex items-center"
          onClick={(e) => {
            e.preventDefault()
            window.history.pushState({}, '', '/')
            window.dispatchEvent(new Event('popstate'))
            window.scrollTo(0, 0)
            closeMobileMenu()
          }}
        >
          <img 
            src="/assets/images/2020/03/busy-beaver-logo.png" 
            alt="Busy Beaver Construction Logo" 
            width={logoWidth}
            height={40}
            className="h-auto pl-4"
          />
        </a>
        <a 
          href="/services/landscaping" 
          className="bg-[#e10e19] text-white text-center rounded py-1 px-2  mt-1 mx-4 text-sm lg:text-lg font-bold hover:brightness-110 transition-all"
          onClick={(e) => {
            e.preventDefault()
            window.history.pushState({}, '', '/services/landscaping')
            window.dispatchEvent(new Event('popstate'))
            window.scrollTo(0, 0)
            closeMobileMenu()
          }}
        >
          Also Landscaping Expert: See Our Work
        </a>
      </div>
    )
  }

  // =====================================
  // DESKTOP HEADER - DO NOT MODIFY THIS SECTION
  // =====================================
  const DesktopHeader = () => (
    <div className="hidden lg:flex justify-between items-center w-full">
      <div className="flex-shrink-0">
        <Logo />
      </div>
      
      <nav className="ml-auto">
        <ul className="flex">
          <NavItem href="/about-us" label="About Us" isTabletLandscape={isTabletLandscape} />
          <NavItem 
            href="/services" 
            label="Services & Gallery" 
            hasChildren={true} 
            isTabletLandscape={isTabletLandscape}
            dropdownItems={serviceGalleryItems}
          />
          {/* <NavItem 
            href="/gallery" 
            label="Gallery" 
            hasChildren={true} 
            isTabletLandscape={isTabletLandscape}
            dropdownItems={galleryItems}
          /> */}
          <NavItem href="/testimonials" label="Reviews" isTabletLandscape={isTabletLandscape} />
          <NavItem href="/estimate" label="Online Calculator" isTabletLandscape={isTabletLandscape} />
          <NavItem href="/appointment" label="Book FREE Estimate" isTabletLandscape={isTabletLandscape} />
        </ul>
      </nav>
      
      <div className="ml-8 mr-auto flex flex-col items-center">
        <a 
          href="tel:4032865400" 
          className={`text-beaver-red font-bold inline-block transform hover:scale-110 transition-all duration-300 ${isTabletLandscape ? 'text-sm' : 'text-sm lg:text-lg'}`}
          onClick={(e) => e.stopPropagation()}
        >
          CALL US (403) 286-5400
        </a>
        <a 
          href="https://www.financeit.ca/en/direct/payment-plan/YT0zMDg2NzMmbD0mcD1wWFE5Y0s0SklWd21tbjhjbTRheUlnJnM9MCZ2PTE=/apply?slug=mRNk6A" 
          className="bg-[#e10e19] text-white text-center rounded py-1 px-2 mt-1 text-lg font-bold hover:brightness-110 transition-all"
          target="_blank"
          rel="noopener noreferrer"
        >
          Finance your Project
        </a>
      </div>
    </div>
  )

  // =====================================
  // MOBILE HEADER - MODIFY THIS SECTION AS NEEDED
  // =====================================
  const MobileHeader = () => (
    <div className="flex lg:hidden justify-between items-center w-full">
      <div className="flex-shrink-0">
        <Logo />
      </div>
      
      <div className="flex flex-col items-end">
        <button 
          className="z-50 p-2 pr-4" 
          onClick={toggleMobileMenu}
          aria-label="Toggle menu"
          aria-expanded={mobileMenuOpen}
        >
          {mobileMenuOpen ? (
            // X icon when menu is open
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          ) : (
            // Hamburger icon when menu is closed
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          )}
        </button>
      </div>
    </div>
  )
  
  return (
    <>
    <header className="sticky top-0 z-50 bg-header-green text-white shadow-md">
      <div className="header-container">
        {/* Render the appropriate header based on screen size */}
        <DesktopHeader />
        <MobileHeader />
      </div>
      
      {/* Mobile menu with slide-down animation */}
      <div 
        className={cn(
          "mobile-menu lg:hidden bg-header-green overflow-hidden transition-all duration-300 ease-in-out shadow-lg",
          mobileMenuOpen ? "max-h-[500px] border-t border-white/10" : "max-h-0"
        )}
      >
        <nav className="container mx-auto px-4 py-2">
          <ul className="flex flex-col space-y-4">
            <NavItem href="/about-us" label="About Us" onClick={closeMobileMenu} isMobile={true} isTabletLandscape={isTabletLandscape} />
            <NavItem 
              href="/services" 
              label="Services & Gallery" 
              hasChildren={true} 
              onClick={closeMobileMenu} 
              isMobile={true} 
              isTabletLandscape={isTabletLandscape}
              dropdownItems={serviceGalleryItems}
            />
            {/* <NavItem 
              href="/gallery" 
              label="Gallery" 
              hasChildren={true} 
              onClick={closeMobileMenu} 
              isMobile={true} 
              isTabletLandscape={isTabletLandscape}
              dropdownItems={galleryItems}
            /> */}
            <NavItem href="/testimonials" label="Reviews" onClick={closeMobileMenu} isMobile={true} isTabletLandscape={isTabletLandscape} />
            <NavItem href="/estimate" label="Online Calculator" onClick={closeMobileMenu} isMobile={true} isTabletLandscape={isTabletLandscape} />
            <NavItem href="/appointment" label="Book FREE Estimate" onClick={closeMobileMenu} isMobile={true} isTabletLandscape={isTabletLandscape} />
            <li className="pt-4 border-t border-white/20">
              <a 
                href="tel:4032865400" 
                className="text-[#e10e19] text-xl font-bold hover:text-red-700 block py-2"
                onClick={(e) => {
                  e.stopPropagation()
                  closeMobileMenu()
                }}
              >
                CALL US (403) 286-5400
              </a>
              <a 
                href="https://www.financeit.ca/en/direct/payment-plan/YT0zMDg2NzMmbD0mcD1wWFE5Y0s0SklWd21tbjhjbTRheUlnJnM9MCZ2PTE=/apply?slug=mRNk6A" 
                className="bg-[#e10e19] text-white text-center py-1 px-2 mt-1 text-sm font-bold rounded hover:brightness-110 transition-all block w-fit"
                target="_blank"
                rel="noopener noreferrer"
                onClick={closeMobileMenu}
              >
                Finance your Project
              </a>
            </li>
          </ul>
        </nav>
      </div>
    </header>
    
    {/* Warning Banner */}
    <div className="bg-[#e10e19] text-white py-3 text-center font-bold shadow-md">
      <div className="container mx-auto px-4">
        <p className="animate-pulse">
          ⚠️ WARNING: If you submitted requests in the past 2 weeks, please resubmit as we were experiencing technical difficulties ⚠️
        </p>
      </div>
    </div>
    </>
  )
} 